import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  AttendanceAudit,
  Class,
  Enrollment,
  Student,
  Teacher,
} from "../models";
import {
  formatSummaryResponse,
  getBreaksReportForClass,
  refreshAttendanceSummary,
} from "../services/attendance-summary-service";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class AttendanceReportController {
  private async resolveJoinDate(
    studentId: mongoose.Types.ObjectId,
    classId: mongoose.Types.ObjectId,
    classItem: { createdAt?: Date },
  ): Promise<Date> {
    const enrollment = await Enrollment.findOne({
      studentId,
      courseOfferingId: classId,
    })
      .select("enrollmentDate")
      .lean();

    if (enrollment?.enrollmentDate) {
      return new Date(enrollment.enrollmentDate);
    }

    return classItem.createdAt ? new Date(classItem.createdAt) : new Date();
  }

  async getStudentClassAttendanceSummary(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(classId);
      const classItem = await Class.findById(classObjectId).lean();
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      const joinDate = await this.resolveJoinDate(
        student._id,
        classObjectId,
        classItem,
      );
      const summary = await refreshAttendanceSummary(
        classObjectId,
        student._id,
        joinDate,
      );

      res.status(200).json({
        success: true,
        message: "Attendance summary retrieved successfully",
        data: formatSummaryResponse(classObjectId, student._id, summary),
      } as APIResponse);
    } catch (error) {
      logger.error("Student attendance summary error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance summary",
      } as APIResponse);
    }
  }

  async getTeacherClassAttendanceSummary(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;
      const { studentId } = req.query;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(classId);
      const classItem = await Class.findById(classObjectId).lean();
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      const isAssigned = classItem.teacherIds?.some(
        id => id.toString() === teacher._id.toString(),
      );
      if (!isAssigned) {
        res.status(403).json({ success: false, message: "Access denied" });
        return;
      }

      if (studentId) {
        const studentObjectId = new mongoose.Types.ObjectId(
          studentId as string,
        );
        const joinDate = await this.resolveJoinDate(
          studentObjectId,
          classObjectId,
          classItem,
        );
        const summary = await refreshAttendanceSummary(
          classObjectId,
          studentObjectId,
          joinDate,
        );
        res.status(200).json({
          success: true,
          message: "Attendance summary retrieved successfully",
          data: formatSummaryResponse(classObjectId, studentObjectId, summary),
        } as APIResponse);
        return;
      }

      const studentIds = classItem.studentIds ?? [];
      const summaries = await Promise.all(
        studentIds.map(async sid => {
          const joinDate = await this.resolveJoinDate(
            sid,
            classObjectId,
            classItem,
          );
          const summary = await refreshAttendanceSummary(
            classObjectId,
            sid,
            joinDate,
          );
          return formatSummaryResponse(classObjectId, sid, summary);
        }),
      );

      res.status(200).json({
        success: true,
        message: "Attendance summaries retrieved successfully",
        data: { summaries },
      } as APIResponse);
    } catch (error) {
      logger.error("Teacher attendance summary error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance summary",
      } as APIResponse);
    }
  }

  async getClassBreaksReport(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!teacher) {
        res.status(404).json({ success: false, message: "Teacher not found" });
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(classId);
      const classItem = await Class.findById(classObjectId).lean();
      if (!classItem) {
        res.status(404).json({ success: false, message: "Class not found" });
        return;
      }

      const isAssigned = classItem.teacherIds?.some(
        id => id.toString() === teacher._id.toString(),
      );
      if (!isAssigned) {
        res.status(403).json({ success: false, message: "Access denied" });
        return;
      }

      const records = await getBreaksReportForClass(classObjectId);

      res.status(200).json({
        success: true,
        message: "Breaks report retrieved successfully",
        data: {
          classId,
          className: classItem.className,
          records,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Breaks report error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve breaks report",
      } as APIResponse);
    }
  }

  async getClassAttendanceAuditLog(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { classId } = req.params;
      const { page = 1, limit = 20 } = req.query;
      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const classObjectIdForAudit = new mongoose.Types.ObjectId(classId);
      const [logs, total] = await Promise.all([
        AttendanceAudit.find({ classId: classObjectIdForAudit })
          .populate("changedBy", "fullName email role")
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        AttendanceAudit.countDocuments({ classId: classObjectIdForAudit }),
      ]);

      res.status(200).json({
        success: true,
        message: "Attendance audit log retrieved successfully",
        data: {
          logs: logs.map(log => ({
            id: log._id?.toString(),
            classId: log.classId?.toString(),
            studentId: log.studentId?.toString(),
            date: log.date,
            changedBy: log.changedBy,
            changedByRole: log.changedByRole,
            fromStatus: log.fromStatus ?? null,
            toStatus: log.toStatus,
            reason: log.reason ?? null,
            changedAt: log.createdAt,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
            hasNextPage: pageNum < Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Attendance audit log error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve audit log",
      } as APIResponse);
    }
  }
}

export const attendanceReportController = new AttendanceReportController();
